import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { makeStyles, Theme } from "@material-ui/core/styles"
import { Outlet, useLocation, useNavigate } from "react-router-dom"

import { logger } from "util/logger"
import { Status } from "shared/types/status"
import { AuthHelper } from "util/authHelper"
import { RootState } from "../../redux/store"
import useAppState from "hooksV1/useAppState"
import { Project } from "shared/types/project"
import { generateClient } from "aws-amplify/api"
import { getProjects } from "redux/slices/projects"
import { listDocumentsByCountryId } from "graphql/queries"
import useBooleanState from "hooksV1/useBooleanStates"
import SideNaveV1 from "components/SideNav/SideNaveV1"
import DocumentsToolBar from "components/DocumentsToolBar"
import useSnackBar, { SnackType } from "hooksV1/useSnackBar"
import { fetchProjectsByUserV2 } from "redux/thunks/projectsThunk"
import { useGetDocumentVersionAccessRightsV1Query } from "redux/services"
import { containsPath, getUpdatedRoles, sortDocumentsOrder } from "util/helper"
import ManageDocumentsToolBar from "components/ManageDocumentsToolBar/ManageDocumentsToolBar"

const client = generateClient()

const useStyles = makeStyles((theme: Theme) => ({
  main: {
    flexGrow: 1,
    padding: "0.75rem",
  },
  root: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    marginTop: "0.5rem",
  },
  sideNav: {
    width: "12.5%",
    height: "100%",
  },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
}))

const ProjectPageV1: React.FC = () => {
  const classes = useStyles()
  let navigate = useNavigate()
  const snackBar = useSnackBar()
  const dispatch = useDispatch()
  const location = useLocation()
  const booleanState = useBooleanState()

  const {
    activeCountry,
    activeProject,
    activeDocument,
    activeProjectUser,
    activeDocumentVersion,
    setDocuments,
    setDocumentRoles,
    setActiveProject,
    setActiveDocument,
    setActiveProjectUser,
    setActiveDocumentVersion,
  } = useAppState()

  const projects: Project[] = useSelector(getProjects)

  const validatedUser = useSelector(
    (state: RootState) => state.authorisation.user
  )

  const loginStatus = useSelector(
    (state: RootState) => state.authorisation.loginStatus
  )

  const [docAccess, setDocAccess] = useState([])
  const [currentPath, setCurrentPath] = useState<string[]>([])

  const pathArray = location.pathname.split("/")
  const projectId = pathArray[2]
  const activeDocId = pathArray[4]
  const activeCountryName = pathArray[6]?.replace(/%20/g, " ")

  const { data: documentVersionData, isLoading } =
    useGetDocumentVersionAccessRightsV1Query(
      {
        documentId: activeDocument?.id,
        id: activeDocumentVersion?.id,
      },
      { skip: !activeDocument || !activeDocumentVersion }
    )

  useEffect(() => {
    if (!documentVersionData) return

    const access = getUpdatedRoles(
      documentVersionData.data?.getDocumentVersion?.access,
      documentVersionData.data?.getDocumentVersion?.access
    )

    setDocAccess(access)
    setDocumentRoles(access)
  }, [documentVersionData])

  useEffect(() => {
    if (projects.length === 0) return

    const activeProject = projects.find((project) => project.id === projectId)

    setActiveProject(activeProject)

    const activeUser = activeProject?.users.find(
      (user) => user.user.id === validatedUser?.id
    )
    setActiveProjectUser(activeUser)
  }, [projects])

  useEffect(() => {
    if (loginStatus === Status.idle || loginStatus === Status.loading)
      return null

    localStorage.setItem("chapterIndex", "1.")

    if (projects.length === 0) onFetchProjects()
  }, [loginStatus])

  useEffect(() => {
    if (!activeCountry || !activeProject || !hasCountryOrDocumentChanged())
      return

    fetchCountryData()
  }, [activeCountry, activeProject, location])

  useEffect(() => {
    booleanState.setIsDocumentVersionLoading(isLoading)
  }, [isLoading])

  const onFetchProjects = () => {
    let fetchProjThunk = fetchProjectsByUserV2(validatedUser?.id)
    dispatch(fetchProjThunk)
  }

  const hasCountryOrDocumentChanged = (): boolean => {
    const pathArray = location.pathname.split("/")

    return (
      pathArray[2] !== currentPath[2] ||
      pathArray[4] !== currentPath[4] ||
      pathArray[6] !== currentPath[6]
    )
  }

  const fetchCountryData = async () => {
    try {
      setLoadingStates(true)

      const documents = await fetchAndFilterDocuments()

      const { activeDocument, shouldNavigate } = processDocuments(documents)

      if (shouldNavigate) {
        handleNavigation(activeDocument)
      }
    } catch (error) {
      handleError(error)
    } finally {
      cleanup()
    }
  }

  // Helper functions
  const setLoadingStates = (isLoading: boolean) => {
    booleanState.setIsDocumentsLoading(isLoading)
    booleanState.setIsDocumentVersionLoading(isLoading)
  }

  const fetchAndFilterDocuments = async (): Promise<any[]> => {
    const result = await client.graphql({
      query: listDocumentsByCountryId,
      variables: {
        filter: {
          countryId: { eq: activeCountry?.id },
          enabled: { eq: true },
        },
      },
    })

    if (result.errors) {
      throw new Error(result.errors.map((e) => e.message).join(", "))
    }

    const documents = result?.data?.listDocuments?.items ?? []

    return sortDocumentsOrder(
      documents.filter((document) =>
        AuthHelper.canViewDocument(
          activeCountry?.id,
          document.documentVersions.items[0].access,
          activeProjectUser
        )
      )
    )
  }

  const processDocuments = (documents: any[]) => {
    let activeDocument = documents.find((doc) => doc.id === activeDocId)
    let shouldNavigate = false

    if (!activeDocument) {
      activeDocument = documents.find(
        (doc) => doc.refId === activeDocument?.refId
      )
      shouldNavigate = true
    }

    setDocuments(documents)

    if (activeDocument) {
      setActiveDocument(activeDocument)
      setActiveDocumentVersion(activeDocument.documentVersions["items"][0])
    }

    return { activeDocument, shouldNavigate }
  }

  const handleNavigation = (activeDocument?: any) => {
    const currentPath = pathArray[pathArray.length - 1]
    const basePath = `/project/${projectId}/document/${activeDocument?.id}/country/${activeCountryName}`

    if (!activeDocument) {
      navigate(`/project/${projectId}/country/${activeCountryName}`)
    } else if (containsPath(currentPath)) {
      navigate(`${basePath}/${currentPath}`)
    } else {
      navigate(basePath)
    }
  }

  const handleError = (error: unknown) => {
    logger("ProjectDocumentV1", "useEffect (fetchCountryData)", error)
    snackBar.setMessage(
      "An error occurred fetching the countries information. Please try again."
    )
    snackBar.setMessageSeverity(SnackType.SnackError)
    snackBar.onOpen()
  }

  const cleanup = () => {
    setCurrentPath(pathArray)
    setLoadingStates(false)
  }

  return (
    <main className={classes.main}>
      <div className={classes.root}>
        <DocumentsToolBar />
        <div className={classes.container}>
          <div className={classes.sideNav}>
            <SideNaveV1 isProjectDocuments documentAccess={docAccess} />
          </div>
          <div className={classes.body}>
            <div
              style={{
                paddingLeft: "0.5rem",
              }}
            >
              <ManageDocumentsToolBar />
            </div>
            <Outlet />
          </div>
        </div>
      </div>
    </main>
  )
}

export default ProjectPageV1
